import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageBreadcrumbs } from '@/components/common/PageBreadcrumbs'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { EditorJsField } from '@/components/editor/EditorJsField'
import { homeworksApi } from '@/api/homeworks'
import { isCheckConflictError, reviewQueueApi } from '@/api/exercises'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'
import type { Homework, HomeworkItem } from '@/types'

/**
 * Проверка одного пункта домашки.
 *
 * Единица проверки у учителя — работа, а не домашка целиком: домашка из пяти
 * пунктов иначе висела бы в очереди, пока он не дойдёт до последнего. Когда
 * проверены все отвеченные пункты, домашка закрывается сама — отдельного
 * «принять» не требуется.
 */
export function HomeworkItemCheckPage() {
  const { userId, homeworkId, itemId } = useParams<{ userId: string; homeworkId: string; itemId: string }>()
  const studentId = Number(userId)
  const workId = Number(homeworkId)
  const targetId = Number(itemId)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const [homework, setHomework] = useState<Homework | null>(null)
  const [item, setItem] = useState<HomeworkItem | null>(null)
  const [score, setScore] = useState(100)
  const [comment, setComment] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Отдельно от error: load() первым делом делает setError(null), и
  // сообщение о конфликте, поставленное перед перечитыванием, стиралось бы
  // до первого же рендера — учитель не видел вообще ничего.
  const [conflict, setConflict] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setConflict(false)
    try {
      const loaded = await homeworksApi.getByUserAndId(studentId, workId)
      const target = (loaded.items ?? []).find((each) => each.id === targetId) ?? null

      setHomework(loaded)
      setItem(target)
      setScore(target?.teacher_score_percent ?? 100)
      setComment(target?.teacher_comment ?? '')
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [studentId, workId, targetId, t])

  useEffect(() => {
    if ([studentId, workId, targetId].every((value) => Number.isFinite(value))) {
      void load()

      return
    }

    setLoading(false)
    setError(t('common.error'))
  }, [load, studentId, workId, targetId, t])

  const save = async () => {
    if (!item) return
    setSaving(true)
    setError(null)
    setConflict(false)
    try {
      await reviewQueueApi.checkHomeworkItem(
        item.id,
        score,
        comment.trim() === '' ? null : comment,
        // Версия пункта, которую проверяющий видел на экране: сервер отклонит
        // оценку, если работу успел переоценить кто-то ещё
        item.updated_at ?? null,
      )
      navigate(ROUTES.EXERCISE_REVIEW)
    } catch (err) {
      // Конфликт версий — не ошибка ввода: работу переоценили, пока страница
      // была открыта. Перечитывать автоматически НЕЛЬЗЯ: load() затирает балл
      // и комментарий, которые учитель только что набрал, чужим вердиктом, а
      // следующий клик записал бы этот чужой вердикт под именем учителя.
      // Показываем конфликт и оставляем решение ему.
      if (isCheckConflictError(err)) {
        setConflict(true)

        return
      }

      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  const studentName = homework?.student
    ? [homework.student.surname, homework.student.first_name].filter(Boolean).join(' ')
    : null

  return (
    <DashboardLayout>
      {/* Те же отступы, что на соседних страницах: без обёртки контент
          прижимался к краю, и переход из очереди дёргал вёрстку */}
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageBreadcrumbs
          items={[
            { label: t('exercises.review.title'), to: ROUTES.EXERCISE_REVIEW },
            { label: t('homeworks.itemCheck.title') },
          ]}
        />
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(ROUTES.EXERCISE_REVIEW)} aria-label={t('common.back')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flex: 1 }}>
            {t('homeworks.itemCheck.title')}
          </Typography>
          <PageHelpPopover pageKey="homework-item-check" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Перечитывание — по кнопке, а не само: оно заменит набранный балл и
            комментарий текущим вердиктом, и это должно быть решением учителя */}
        {conflict && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void load()}>
                {t('exercises.check.reload')}
              </Button>
            }
          >
            {t('exercises.check.conflict')}
          </Alert>
        )}

        {loading && <Skeleton variant="rounded" height={420} />}

        {!loading && !item && !error && (
          <Alert severity="info">{t('homeworks.itemCheck.notFound')}</Alert>
        )}

        {!loading && item && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={studentName ?? `#${studentId}`} />
              <Chip
                size="small"
                variant="outlined"
                label={t('homeworks.itemNumber', { n: item.position + 1 })}
              />
              {item.checked_at && (
                <Chip size="small" color="success" label={t('homeworks.statusChecked')} />
              )}
            </Stack>

            {/* Переоценка разрешена: оценку ставят руками и в один клик, и
              случайная сотня иначе осталась бы у ученика навсегда */}
            {item.checked_at && <Alert severity="info">{t('homeworks.itemCheck.regrade')}</Alert>}

            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  {t('homeworks.itemCheck.task')}
                </Typography>
                {item.task_json ? (
                  <EditorJsField
                    key={`check-task-${item.id}`}
                    value={item.task_json}
                    onChange={() => {}}
                    readOnly
                    extendedBlocks
                    readOnlySurface="paper"
                    hideLabel
                    initDelayMs={50}
                  />
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {item.task_text ?? '—'}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  {t('homeworks.itemCheck.answer')}
                </Typography>
                {item.answer_json ? (
                  <EditorJsField
                    key={`check-answer-${item.id}`}
                    value={item.answer_json}
                    onChange={() => {}}
                    readOnly
                    extendedBlocks
                    readOnlySurface="paper"
                    hideLabel
                    initDelayMs={50}
                  />
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {item.answer_text ?? t('homeworks.itemCheck.noAnswer')}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  {t('exercises.check.score')}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Slider
                    value={score}
                    min={0}
                    max={100}
                    step={5}
                    marks
                    valueLabelDisplay="auto"
                    onChange={(_, value) => setScore(Number(value))}
                    sx={{ flex: 1 }}
                  />
                  <Typography sx={{ width: 56, textAlign: 'right' }}>{score}%</Typography>
                </Stack>

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  sx={{ mt: 2 }}
                  label={t('exercises.check.comment')}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                />

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button variant="contained" disabled={saving} onClick={() => void save()}>
                    {saving ? t('common.loading') : t('exercises.check.submit')}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}
      </Box>
    </DashboardLayout>
  )
}
